import { z } from 'zod';
import { VersandStatus } from './nutzer';

/** Übliche SMTP-Ports – andere Ports lässt der Server nicht zu (kein Zugriff auf beliebige Dienste) */
export const SMTP_PORTS = [587, 465, 25, 2525] as const;

export const SMTP_SICHERHEIT = {
  starttls: 'STARTTLS',
  ssl: 'SSL/TLS',
  keine: 'Keine',
} as const;
export type SmtpSicherheit = keyof typeof SMTP_SICHERHEIT;

const Email = z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190);

/** Einstellungen → E-Mail-Versand. Wie mail_cfg der PHP-Middleware, dazu Antwortadresse und Schalter. */
export const EmailZugangSchema = z.object({
  host: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Bitte SMTP-Server angeben')
    .max(190)
    .regex(/^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/, 'Bitte den Namen des Servers angeben, z. B. smtp.ihr-anbieter.de'),
  port: z.coerce.number().refine((p) => (SMTP_PORTS as readonly number[]).includes(p), 'Erlaubt sind die Ports 587, 465, 25 und 2525'),
  sicherheit: z.enum(['starttls', 'ssl', 'keine']),
  benutzer: z.string().trim().max(190).default(''),
  /** leer = bisheriges Passwort behalten */
  passwort: z.string().max(300).optional(),
  passwortLoeschen: z.boolean().optional(),
  absenderEmail: Email,
  absenderName: z.string().trim().max(120).default(''),
  antwortAn: z.union([Email, z.literal('')]).default(''),
  aktiv: z.boolean().default(true),
});
export type EmailZugangEingabe = z.input<typeof EmailZugangSchema>;

export interface EmailZugang {
  host: string;
  port: number;
  sicherheit: SmtpSicherheit;
  benutzer: string;
  passwortGesetzt: boolean;
  absenderEmail: string;
  absenderName: string;
  antwortAn: string;
  aktiv: boolean;
  geprueftAm: string | null;
}

export interface EmailEinstellungen {
  zugang: EmailZugang | null;
  /** Versand über DXL möglich (Rückfall ohne eigenen Zugang) */
  dxlVerfuegbar: boolean;
  /** So erscheint der Absender beim Versand über DXL */
  dxlAbsender: string;
}

export const TestMailSchema = z.object({ an: Email });

export const MAIL_ARTEN = {
  einladung: 'Einladung',
  zuruecksetzen: 'Passwort zurücksetzen',
  test: 'Test-Mail',
  haendler_status: 'Händler-Status',
  portal_bestaetigen: 'Händlerportal: E-Mail bestätigen',
  portal_zuruecksetzen: 'Händlerportal: Passwort zurücksetzen',
  portal_einladung: 'Händlerportal: Einladung ins Team',
  rechnung: 'Rechnung',
  ankauf_bestaetigung: 'Ankauf: Eingangsbestätigung',
  ankauf_team: 'Ankauf: Hinweis an das Team',
  ankauf_kunde: 'Ankauf: Nachricht an den Kunden',
  ankauf_anmeldung: 'Ankauf: Anmelde-Link zum Verkäuferportal',
  registrierung: 'Registrierung: E-Mail bestätigen',
  registrierung_dxl: 'Neue Registrierung (Hinweis an DXL)',
  paket_gebucht: 'Paket gebucht',
  abo_stripe: 'Abo und Zahlung (Stripe)',
  marke_einladung: 'Retail Media: Einladung einer Marke',
  retoure_angemeldet: 'Retoure: angemeldet (Kunde)',
  retoure_label: 'Retoure: Label nachgereicht (Kunde)',
  retoure_erinnerung: 'Retoure: Erinnerung (Kunde)',
  retoure_storniert: 'Retoure: zurückgezogen (Kunde)',
  retoure_angekommen: 'Retoure: Paket angekommen (Kunde)',
  retoure_erstattet: 'Retoure: Erstattung (Kunde)',
  retoure_klaerfall: 'Retoure: Ergebnis der Prüfung (Kunde)',
  retoure_haendler_neu: 'Retoure: neue Retoure (Händler)',
  retoure_haendler_label: 'Retoure: Label bereitstellen (Händler)',
  retoure_haendler_label_erinnerung: 'Retoure: Erinnerung Label (Händler)',
  retoure_test: 'Retoure: Test-Mail einer Vorlage',
  retoure_abholung: 'Retoure: Abholtermin (Kunde)',
  retoure_haendler_zugestellt: 'Retoure: zugestellt, bitte buchen (Händler)',
  retoure_haendler_frist: 'Retoure: Frist läuft ab (Händler)',
} as const;
export type MailArt = keyof typeof MAIL_ARTEN;

export interface MailProtokollEintrag {
  id: string;
  art: string;
  an: string;
  betreff: string;
  weg: 'marktplatz' | 'dxl' | 'keiner';
  status: z.infer<typeof VersandStatus>;
  fehler: string | null;
  zeitpunkt: string;
}

export interface MailProtokollSeite {
  eintraege: MailProtokollEintrag[];
  /** Cursor für die nächste Seite, null = Ende */
  weiter: string | null;
}

export interface TestMailErgebnis {
  status: z.infer<typeof VersandStatus>;
  fehler: string | null;
}

/* ------------------------------------------------------------ Plattform (DXL) */

const Guid = z.string().trim().toLowerCase().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'Bitte die ID im Format 00000000-0000-0000-0000-000000000000 angeben');

/** Microsoft 365 über die Graph-API: App-Registrierung in Entra ID mit Anwendungsberechtigung Mail.Send */
export const GraphZugangSchema = z.object({
  typ: z.literal('graph'),
  tenantId: z.union([Guid, z.string().trim().toLowerCase().regex(/^([a-z0-9-]+\.)+[a-z]{2,}$/, 'Bitte Verzeichnis-ID (GUID) oder Domain angeben')]),
  clientId: Guid,
  /** leer = bisheriges Secret behalten */
  clientSecret: z.string().trim().max(300).optional(),
  postfach: Email,
  antwortAn: z.union([Email, z.literal('')]).default(''),
  aktiv: z.boolean().default(true),
});

export const PlattformMailSchema = z.discriminatedUnion('typ', [EmailZugangSchema.extend({ typ: z.literal('smtp') }), GraphZugangSchema]);
export type PlattformMailEingabe = z.input<typeof PlattformMailSchema>;

export type PlattformMailZugang =
  | ({ typ: 'smtp' } & EmailZugang)
  | {
      typ: 'graph';
      tenantId: string;
      clientId: string;
      secretGesetzt: boolean;
      postfach: string;
      antwortAn: string;
      aktiv: boolean;
      geprueftAm: string | null;
    };

export interface PlattformMailEinstellungen {
  zugang: PlattformMailZugang | null;
  /** SMTP_URL ist gesetzt – Rückfall ohne eigenen Zugang */
  umgebung: boolean;
}
